import React from 'react'

import { Formik } from 'formik'
import { useHistory } from 'react-router-dom'

import { AdminContext } from 'contexts/AdminContext'
import { ZariContext } from '_zari/context'

import { Divider, Button, Form, Col, Row } from '_zari/core'

import {
  CodigosTransaccionesConstant,
  CondicionesPagoConstant,
  MonedasConstant,
  TiposDocumentoIdentificacionConstant,
  TiposDocumentoSunatConstant,
  TiposProductoConstant,
  LadosIsla,
  ModalidadCalculoIsla,
  MontosIcbper
} from 'constants/index'

import { IdGen, RounderUtil, ZrSwal } from 'utils'

import * as FacturacionService from 'services/facturacion.service'
import * as SocioService from 'services/socio.service'

import { RetrocederButton } from 'sharedComponents/buttons'

import * as FormGroups from './formGroups'
import * as Modals from './modals'
import * as Sections from './sections'

import { ValidationSchema } from './validation'

const commonReducer = (state, action) => {
  switch (action.type) {
    case 'agregar': {
      return [
        ...state,
        {
          ...action.payload,
          _idFila: IdGen.newId()
        }
      ]
    }
    case 'eliminar':
      return state.filter(val => val._idFila !== action.payload._idFila)
    default:
      return state
  }
}

const NuevaMainForm = ({ editarData, flagEditarComentarios, codigoTransaccion, reiniciarFormulario }) => {
  const __adminContext = React.useContext(AdminContext)
  const __zariContext = React.useContext(ZariContext)

  const [detallesAdicionales, detallesAdicionalesDispatcher] = React.useReducer(commonReducer, [])

  const history = useHistory()

  const mainFormikRef = React.useRef(null)
  const mainFormDataRef = React.useRef(null)
  const idClienteVarios = React.useRef(null)
  const pdfHashRef = React.useRef(null)

  const [mostrarFormularioPagosModal, setMostrarFormularioPagosModal] = React.useState(false)
  const [mostrarPreviewPdfModal, setMostrarPreviewPdfModal] = React.useState(false)
  const [mostrarProductoAdicionalModal, setMostrarProductoAdicionalModal] = React.useState(false)

  const [datosCombustibleEnUso, setDatosCombustibleEnUso] = React.useState(
    editarData.detalles
      ? editarData.detalles.map(x => ({
        _idProducto: x._idProducto,
        _idProductoPresentacion: x._idProductoPresentacion,
        cantidad: x.cantidad,
        precio: x.precio,
        total: x.total
      }))[0]
      : {}
  )

  const [idListaPrecio, setIdListaPrecio] = React.useState(
    editarData._idListaPrecio || __adminContext.valoresConfiguracionSede._idListaPrecio || ''
  )

  React.useEffect(() => {
    SocioService.obtenerIdClienteVarios()
      .then(({ data }) => {
        idClienteVarios.current = data
      })
      .catch(({ data: { error } }) => __zariContext.snack('error', error))
  }, [__zariContext])

  const formInitialValues = React.useMemo(() => {
    let condicionPago = editarData.condicionPago || CondicionesPagoConstant.CONTADO
    let tipoDocumento = editarData.tipoDocumento || TiposDocumentoSunatConstant.BOLETA
    let serieDocumento = editarData.serieDocumento || ''
    let numeroDocumento = editarData.numeroDocumento || ''

    if (editarData._idDocumentoNota) {
      condicionPago = CondicionesPagoConstant.CREDITO
      tipoDocumento = TiposDocumentoSunatConstant.NOTA_CREDITO
      serieDocumento = ''
      numeroDocumento = ''
    }

    return {
      _id: editarData._id || '',
      _idDocumentoNota: editarData._idDocumentoNota || '',
      idsAnticipos: editarData.idsAnticipos || [],
      _idDireccionSocio: editarData._idDireccionSocio || '',
      _idModeloCuentas12: editarData._idModeloCuentas12 || '',
      _idPedido: editarData._idPedido || '',
      _idPedidoEcommerce: editarData._idPedidoEcommerce || '',
      _idPlacaSocio: editarData._idPlacaSocio || '',
      _idPlantillaComentario: editarData._idPlantillaComentario || '',
      _idSocio: editarData._idSocio || '',
      _idVendedor: editarData._idVendedor || '',
      codigoSocio: editarData.codigoSocio || '',
      codigoTransaccion: codigoTransaccion,
      comentario: editarData.comentario || '',
      condicionPago: condicionPago,
      documentoSocio: editarData.documentoSocio || '',
      fechaEmision: editarData.fechaEmision || __adminContext.fechaTrabajo,
      fechaVencimiento: editarData.fechaVencimiento || editarData.fechaEmision || __adminContext.fechaTrabajo,
      flagSerieContingencia: editarData.flagSerieContingencia ?? false,
      flagSinEntrega: false,
      flagVentaRapida: false,
      flagGrifo: true,
      flagRetiro: false,
      flagPorGuias: editarData.flagPorGuias || false,
      incluyeIGV: editarData.incluyeIGV || true,
      infosAnticipos: editarData.infosAnticipos || [],
      ladoIsla: editarData.ladoIsla || LadosIsla.Lado1,
      modalidadCalculoIsla: editarData.modalidadCalculoIsla || ModalidadCalculoIsla.MONTO_TOTAL,
      moneda: editarData.moneda || MonedasConstant.SOLES,
      montoAnticipo: editarData.montoAnticipo || 0,
      motivo: editarData.motivo || '',
      nombreAlternativoSocio: editarData.nombreAlternativoSocio || '',
      nombreSocio: editarData.nombreSocio || '',
      numeroDocumento: numeroDocumento,
      ordenCompra: editarData.ordenCompra || '',
      serieDocumento: serieDocumento,
      tipoDocumento: tipoDocumento,
      tipoDocumentoSocio: editarData.tipoDocumentoSocio || '',
      tipoNota: editarData.tipoNota || '',
      tipoProducto: editarData.tipoProducto || TiposProductoConstant.BIEN
    }
  }, [editarData, codigoTransaccion, __adminContext.fechaTrabajo])

  const submitFormularioPagos = React.useCallback(
    (formData, { setSubmitting } = {}) => {
      if (!formData._idSocio) {
        formData._idSocio = idClienteVarios.current
      }

      formData._idListaPrecio = idListaPrecio

      const accion = formData._id
        ? () => FacturacionService.actualizarFacturacionxId(formData._id, formData)
        : () => FacturacionService.nuevaFacturacion(formData)

      accion()
        .then(({ data: data2 }) => {
          if (formData._id) {
            history.push(`/${CodigosTransaccionesConstant.VENTAS_LISTA}`)
          } else {
            pdfHashRef.current = data2.hash
            setMostrarPreviewPdfModal(true)
          }

          __zariContext.toast('success', 'El documento se ha guardado.')
        })
        .catch(({ data: { error } }) => {
          __zariContext.snack('error', error)
          setSubmitting(false)
        })
    },
    [__zariContext, history, idListaPrecio]
  )

  const onSubmit = React.useCallback(
    (formData, { setSubmitting }) => {
      const cantidad = parseFloat(datosCombustibleEnUso.cantidad || 0)
      const precio = parseFloat(datosCombustibleEnUso.precio || 0)
      const valorUnitario = RounderUtil.round(precio / 1.18, 5)

      const montoTotal = parseFloat(datosCombustibleEnUso.total || 0)
      const montoIgv = RounderUtil.roundTwoDecimals(montoTotal - parseFloat(montoTotal) / 1.18)
      const montoGravado = RounderUtil.roundTwoDecimals(montoTotal - montoIgv)

      let importeGravado = montoGravado
      let importeInafecto = 0
      let importeExonerado = 0
      let importeIgv = montoIgv
      let importeIcbper = 0
      let importeTotal = montoTotal

      const detallesNormalizados = datosCombustibleEnUso && datosCombustibleEnUso.total && datosCombustibleEnUso.cantidad ? [
        {
          _idProductoPresentacion: datosCombustibleEnUso._idProductoPresentacion,
          _idProductoPresentacionPrecio: datosCombustibleEnUso._idProductoPresentacionPrecio,
          _idPlacaSocio: formData._idPlacaSocio,
          cantidad: cantidad,
          precio: precio,
          precioReferencia: precio,
          valorUnitario: valorUnitario,
          gravado: montoGravado,
          valorBrutoTotal: montoGravado,
          igv: montoIgv,
          total: montoTotal,
          descuentoMonto: 0,
          descuentoPorcentaje: 0,
          icbper: 0,
          comentario: '',
          afectacion: '10',
          flagAnticipo: false
        }
      ] : []

      const fechaActual = new Date()
      const anoActual = `${fechaActual.getFullYear()}`
      let factorIcbper = MontosIcbper.montos[anoActual] || MontosIcbper.montos['2023']

      if (detallesAdicionales && detallesAdicionales.length > 0) {
        for (let i = 0; i < detallesAdicionales.length; i++) {
          const x = detallesAdicionales[i]

          let precio = 0
          let valorUnitario = 0
          let gravado = 0
          let exonerado = 0
          let inafecto = 0
          let valorBrutoTotal = 0
          let igv = 0
          let total = 0
          let precioReferencia = 0

          let descuentoConIGV = 0
          let descuentoSinIGV = 0
          let descuentoIGV = 0

          let icbper = parseFloat(x.cantidad) * (x.flagBolsa ? factorIcbper : 0)

          if (x.afectacion === '10' || x.afectacion === '11') {
            precio = parseFloat(x.precio || 0)
            precioReferencia = precio
            valorUnitario = RounderUtil.round(parseFloat(x.precio || 0) / 1.18, 5)
            igv = parseFloat(x.total) - parseFloat(x.total) / 1.18
            gravado = parseFloat(x.total) - igv
            valorBrutoTotal = gravado
            total = parseFloat(x.total)
          } else if (x.afectacion === '20') {
            valorUnitario = parseFloat(x.precio || 0)
            precio = RounderUtil.round(valorUnitario)
            precioReferencia = precio
            exonerado = parseFloat(x.total || 0)
            valorBrutoTotal = exonerado
            igv = 0
            total = exonerado
          } else if (x.afectacion === '30') {
            valorUnitario = parseFloat(x.precio || 0)
            precio = RounderUtil.round(valorUnitario)
            precioReferencia = precio
            inafecto = parseFloat(x.total || 0)
            valorBrutoTotal = inafecto
            igv = 0
            total = inafecto
          }

          importeGravado = (importeGravado + gravado)
          importeInafecto = RounderUtil.roundTwoDecimals(importeInafecto + inafecto)
          importeExonerado = RounderUtil.roundTwoDecimals(importeExonerado + exonerado)
          importeIgv = RounderUtil.roundTwoDecimals(importeIgv + igv)
          importeIcbper = RounderUtil.roundTwoDecimals(importeIcbper + icbper)
          importeTotal = importeTotal + total

          detallesNormalizados.push({
            _idProductoPresentacion: x._idProductoPresentacion,
            _idProductoPresentacionPrecio: x._idProductoPresentacionPrecio,
            _idPlacaSocio: null,
            cantidad: x.cantidad,
            precio: precio,
            precioReferencia: precioReferencia,
            valorUnitario: valorUnitario,
            gravado: gravado,
            exonerado: exonerado,
            inafecto: inafecto,
            valorBrutoTotal: valorBrutoTotal,
            icbper: icbper,
            igv: igv,
            total: total,
            descuentoConIGV: descuentoConIGV,
            descuentoSinIGV: descuentoSinIGV,
            descuentoIGV: descuentoIGV,
            comentario: x.comentario,
            flagGratuito: x.flagGratuito,
            afectacion: x.afectacion,
            flagAnticipo: x.flagAnticipo
          })
        }
      }

      importeIgv = RounderUtil.roundTwoDecimals(importeTotal - (importeTotal / 1.18))

      importeTotal = RounderUtil.roundTwoDecimals(importeTotal)

      const submitData = {
        ...formData,
        numeroDocumento: parseFloat(formData.numeroDocumento),
        detalles: detallesNormalizados,
        importeGravado: importeGravado,
        importeInafecto: importeInafecto,
        importeExonerado: importeExonerado,
        importeIgv: importeIgv,
        importeIcbper: importeIcbper,
        importeTotal: importeTotal
      }

      if (
        submitData.tipoDocumento === TiposDocumentoSunatConstant.BOLETA &&
        submitData.tipoDocumentoSocio !== TiposDocumentoIdentificacionConstant.DNI &&
        submitData.importeTotal > 700
      ) {
        __zariContext.snack('error', 'Boleta con monto mayor a 700 debe ser asignado a un DNI.')
        setSubmitting(false)
        return
      }

      mainFormDataRef.current = submitData

      if (submitData.tipoDocumento === TiposDocumentoSunatConstant.FACTURA && !submitData._idPlacaSocio) {
        ZrSwal.showAlert({
          mensaje: 'Desea continuar sin placa?',
          showCerrar: true,
          tipo: 'warning',
          onSuccess: () => {
            if (submitData.condicionPago === CondicionesPagoConstant.CONTADO) {
              setMostrarFormularioPagosModal(true)
            } else {
              submitFormularioPagos(mainFormDataRef.current)
            }
          },
          onClose: () => {
            setSubmitting(false)
          }
        })
      } else {
        if (submitData.condicionPago === CondicionesPagoConstant.CONTADO) {
          setMostrarFormularioPagosModal(true)
        } else {
          submitFormularioPagos(mainFormDataRef.current)
        }
      }
    },
    [__zariContext, submitFormularioPagos, datosCombustibleEnUso, detallesAdicionales]
  )

  const importeSumaTotal = React.useMemo(() => {
    let salida = 0

    let importeCombustible = parseFloat(datosCombustibleEnUso.total || '0')

    salida = RounderUtil.round(salida + importeCombustible)

    if (detallesAdicionales && detallesAdicionales.length > 0) {
      for (let i = 0; i < detallesAdicionales.length; i++) {
        const detAdi = detallesAdicionales[i]

        salida = RounderUtil.round(salida + detAdi.total)
      }
    }

    return salida
  }, [detallesAdicionales, datosCombustibleEnUso.total])

  return (
    <Row>
      <Col md={12}>
        <Formik
          initialValues={formInitialValues}
          innerRef={mainFormikRef}
          onSubmit={onSubmit}
          validationSchema={ValidationSchema}
          validateOnMount={false}
          validateOnChange={false}
          validateOnBlur={false}
        >
          {({ errors, isSubmitting, setValues, touched, values, setFieldValue, handleSubmit, handleChange }) => (
            <>
              <Form>
                <Row>
                  <Col md={5}>
                    <FormGroups.SocioFormGroup
                      iValue={values._idSocio}
                      values={values}
                      setValues={setValues}
                      iError={errors._idSocio}
                    />
                    <FormGroups.DireccionSocioFormGroup
                      iError={errors._idDireccionSocio}
                      iTouched={touched._idDireccionSocio}
                      vIdSocio={values._idSocio}
                      fIsSubmitting={isSubmitting}
                      setFieldValue={setFieldValue}
                      flagEditarComentarios={flagEditarComentarios}
                    />
                    <Row>
                      <Col md={6}>
                        <FormGroups.CondicionPagoFormGroup
                          fIsSubmitting={isSubmitting}
                          iError={errors.condicionPago}
                          iTouched={touched.condicionPago}
                          vIdDocumentoNota={values._idDocumentoNota}
                          flagGrifo={values.flagGrifo}
                        />
                      </Col>
                      <Col md={6}>
                        <FormGroups.MonedaFormGroup
                          fIsSubmitting={isSubmitting}
                          iError={errors.moneda}
                          iTouched={touched.moneda}
                          iValue={values.moneda}
                          flagGrifo={values.flagGrifo}
                        />
                      </Col>
                    </Row>
                    <Row>
                      <Col md={6}>
                        <FormGroups.FechaEmisionFormGroup
                          fIsSubmitting={isSubmitting}
                          iError={errors.fechaEmision}
                          iTouched={touched.fechaEmision}
                          handleChange={handleChange}
                          setValues={setValues}
                          values={values}
                        />
                      </Col>
                      <Col md={6}>
                        <FormGroups.TipoDocumentoFormGroup
                          fIsSubmitting={isSubmitting}
                          iError={errors.tipoDocumento}
                          iTouched={touched.tipoDocumento}
                          vIdDocumentoNota={values._idDocumentoNota}
                          flagEditarComentarios={flagEditarComentarios}
                          flagGrifo={values.flagGrifo}
                          vId={values._id}
                        />
                      </Col>
                    </Row>
                    <Row>
                      <Col md={6}>
                        <FormGroups.PlacaFormGroup
                          setFieldValue={setFieldValue}
                          iError={errors._idPlacaSocio}
                          iTouched={touched._idPlacaSocio}
                          iValue_IdSocio={values._idSocio}
                          fIsSubmitting={isSubmitting}
                          flagEditarComentarios={flagEditarComentarios}
                          flagGrifo={values.flagGrifo}
                        />
                      </Col>
                      <Col md={6}>
                        <FormGroups.SerieNumeroFormGroup
                          fIsSubmitting={isSubmitting}
                          setFieldValue={setFieldValue}
                          vId={values._id}
                          vTipoDocumento={values.tipoDocumento}
                          vSerieDocumento={values.serieDocumento}
                          flagEditarComentarios={flagEditarComentarios}
                          flagGrifo={values.flagGrifo}
                          vSerieReferencia={editarData.serieDocumento}
                          flagSerieContingencia={values.flagSerieContingencia}
                          handleChange={handleChange}
                        />
                      </Col>
                    </Row>
                  </Col>
                  <Col md={5} style={{ borderLeft: '1px solid black' }}>
                    <FormGroups.ModalidadCalculoIslaFormGroup
                      iValue={values.modalidadCalculoIsla}
                      fIsSubmitting={isSubmitting}
                    />
                    <Divider />
                    <Sections.CombustiblesxIslaSection
                      modalidadCalculo={values.modalidadCalculoIsla}
                      datosCombustibleEnUso={datosCombustibleEnUso}
                      setDatosCombustibleEnUso={setDatosCombustibleEnUso}
                      ladoSeleccionado={values.ladoIsla}
                      idListaPrecio={idListaPrecio}
                      setFieldValue={setFieldValue}
                      handleSubmit={handleSubmit}
                      flagEdicion={!!values._id}
                    />
                  </Col>
                  <Col md={2}>
                    {!__adminContext.valoresConfiguracionSede._idListaPrecio && (
                      <FormGroups.IdListaPrecioFormGroup
                        fIsSubmitting={isSubmitting}
                        idListaPrecio={idListaPrecio}
                        setIdListaPrecio={setIdListaPrecio}
                        flagEdicion={!!values._id}
                      />
                    )}
                    {__adminContext.valoresConfiguracionCompania.flagContabilidad && (
                      <FormGroups.ModelosCuentas12FormGroup
                        fIsSubmitting={isSubmitting}
                        iError={errors._idModeloCuentas12}
                        iTouched={touched._idModeloCuentas12}
                        setFieldValue={setFieldValue}
                        vCodigoTransaccion={values.codigoTransaccion}
                        iValue={values._idModeloCuentas12}
                      />
                    )}
                  </Col>
                </Row>
                <Divider />
                <Row>
                  <Col md={5}>
                    {detallesAdicionales.length > 0 && (
                      <Sections.ListaProductosAdicionalesSection
                        detalles={detallesAdicionales}
                        dispatch={detallesAdicionalesDispatcher}
                      />
                    )}
                  </Col>
                  <Col md={5}>
                    <Row>
                      <Col md={6}>
                        <Button
                          id='agregar-producto-adicional'
                          block
                          onClick={() => setMostrarProductoAdicionalModal(true)}
                        >
                          Agregar producto adicional
                        </Button>
                      </Col>
                      <Col md={6}>
                        <Form.Group>
                          <Form.Label text='TOTAL' md={4} alignRightAt='md' notSlim />
                          <Col md={8}>
                            <Form.InputNumber
                              id='montoTotal'
                              name='montoTotal'
                              disabled
                              placeholder={importeSumaTotal}
                            />
                          </Col>
                        </Form.Group>
                      </Col>
                    </Row>
                  </Col>
                </Row>
                <Divider noMargin />
                <Row>
                  <Col md={12} className='text-right'>
                    <RetrocederButton />
                    <Button disabled={isSubmitting} onClick={handleSubmit}>
                      Cobrar
                    </Button>
                  </Col>
                </Row>
              </Form>
            </>
          )}
        </Formik>
        {mostrarFormularioPagosModal && (
          <Modals.FormularioPagosModal
            handleClose={e => {
              setMostrarFormularioPagosModal(false)
              mainFormikRef.current.setSubmitting(false)
            }}
            show={mostrarFormularioPagosModal}
            mainFormData={mainFormDataRef.current}
            submitFunction={submitFormularioPagos}
            flagGrifo={true}
          />
        )}
        {mostrarPreviewPdfModal && (
          <Modals.PreviewPdfModal
            handleClose={() => setMostrarPreviewPdfModal(false)}
            show={mostrarPreviewPdfModal}
            reiniciarFormulario={reiniciarFormulario}
            pdfHash={pdfHashRef.current}
          />
        )}
        {mostrarProductoAdicionalModal && (
          <Modals.ProductoAdicionalModal
            handleClose={() => setMostrarProductoAdicionalModal(false)}
            show={mostrarProductoAdicionalModal}
            idListaPrecio={idListaPrecio}
            dispatch={detallesAdicionalesDispatcher}
          />
        )}
      </Col>
    </Row>
  )
}

export default NuevaMainForm
